import React, { useEffect, useMemo, useState } from 'react'
import { AxiosResponse } from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import { Action } from '@siftscience/focus-components/action-bar'
import { Modal } from '@siftscience/focus-components/modal'
import { Text } from '@siftscience/focus-components/text'
import Header from './components/Header'
import ImagesList from './components/ImagesList'
import { ImageCardProps } from './types'
import SelectedTooltip from './components/SelectedTooltip'
import DetailsSidebar from './DetailsSidebar'
import { useMerchantContext } from '../template-tooling/context/merchant-context'
import { ISelectProps } from './components/Actions'
import UploadImage from './components/UploadImage'
import axios from '../../utils/http/axios-local'

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%',
    position: 'relative',
    height: 'calc(100vh - 84px - 24px)',
    overflowY: 'auto'
  }
}))

const SEARCH_DEBOUNCE = 500
const MODAL_ACTIONS: Action[] = [{ label: 'Done' }]

const sortBySelectItems: ISelectProps[] = [
  { label: 'Upload Date', value: 'created_at' },
  { label: 'Name', value: 'name' }
]

const ImageLibrary = () => {
  const classes = useStyles()
  const [isShowModalAction, setIsShowModalAction] = useState(false)
  const [replaceImageId, setReplaceImageId] = useState('')
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedMID, setSelectedMID] = useState<string[]>([])
  const [sortBy, setSortBy] = useState<ISelectProps>(sortBySelectItems[0])
  const [search, setSearch] = useState('')
  const [midsItems, setMidsItems] = useState([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [currentId, setCurrentId] = useState('')
  const [loading, setLoading] = useState(false)
  const [imagesList, setImagesList] = useState<ImageCardProps[]>([])
  const [totalImagesCount, setTotalImagesCount] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const isSelected = (id: string) => selectedIds.has(id)
  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds])
  const { selectedMerchant } = useMerchantContext()

  const fetchImagesList = async (currentPage: number, perPage: number) => {
    setLoading(true)
    try {
      const response = await axios.get('/det/tooling/merchant_library', {
        params: {
          'mid_ids[]': selectedMID,
          sort_by: sortBy,
          desc: true,
          name: search,
          page: currentPage,
          per_page: perPage,
          merchant_id: selectedMerchant.id
        }
      })
      setImagesList(response.data.evidences)
      setTotalImagesCount(response.data.total_count)
      return response
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const fetchMids = async () => {
    setLoading(true)
    try {
      const response = await axios.get(
        `/det/tooling/merchants/${selectedMerchant.id}/mids`
      )
      const options =
        response.data?.mids?.map(item => ({
          value: item.id,
          label: item.name
        })) || []
      setMidsItems(options)
      return response
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (imagesList.length !== selectedArray.length) {
      setSelectedIds(new Set(imagesList.map(i => i.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleOpenDetails = (id: string) => {
    setCurrentId(id)
  }

  const onDelete = async (id: string) => {
    try {
      await axios.delete(`/det/tooling/merchant_library/${id}`, {
        params: {
          merchant_id: selectedMerchant.id
        }
      })
      setCurrentId('')
      void fetchImagesList(currentPage, perPage)
    } catch (error) {
      console.error(error)
    }
  }

  const onEvidenceChange = async (id: string) => {
    setReplaceImageId(id)
    setShowUploadModal(true)
  }

  const onDeleteMany = async () => {
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          axios.delete(`/det/tooling/merchant_library/${id}`, {
            params: { merchant_id: selectedMerchant.id }
          })
        )
      )

      void fetchImagesList(currentPage, perPage)
      setSelectedIds(new Set())
    } catch (error) {
      console.error(error)
    }
  }

  const onUpdateEvidence = async (
    evidenceId: number | string,
    formData: FormData
  ) => {
    formData.append('merchant_id', selectedMerchant.id.toString())
    return axios.patch(
      `/det/tooling/merchant_library/${evidenceId}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' }
      }
    )
  }

  const onUpload = async (file: File) => {
    const formData = new FormData()
    formData.append('evidence[evidence]', file)

    const midsToFill = selectedMID.length
      ? selectedMID
      : midsItems.map(item => item.value)

    midsToFill.forEach(id => {
      formData.append(`evidence[mid_ids][]`, id)
    })

    formData.append('merchant_id', selectedMerchant.id.toString())

    let response: AxiosResponse

    if (replaceImageId) {
      response = await onUpdateEvidence(replaceImageId, formData)
    } else {
      formData.append('evidence[group]', file.name)
      response = await axios.post('/det/tooling/merchant_library', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    }
    await fetchImagesList(currentPage, perPage)
    setIsShowModalAction(true)
    setReplaceImageId('')

    return response
  }

  const onModalAction = (action: Action) => {
    if (action.label === 'Done') {
      setShowUploadModal(false)
    }
  }

  useEffect(() => {
    if (!selectedMerchant?.id) {
      return
    }

    const timerId = setTimeout(() => {
      void fetchImagesList(currentPage, perPage)
    }, SEARCH_DEBOUNCE)

    return () => {
      clearTimeout(timerId)
    }
  }, [selectedMerchant, search, sortBy, selectedMID, perPage, currentPage])

  useEffect(() => {
    if (selectedMerchant?.id) {
      void fetchMids()
    }
  }, [selectedMerchant])

  const onPerPageChange = (value: number) => {
    setPerPage(value)
    setCurrentPage(1)
  }

  const onMidSelect = (value: string[]) => {
    setSelectedMID(value)
    setCurrentPage(1)
  }

  return (
    <div className={classes.container}>
      <Modal
        visible={!!showUploadModal}
        onCloseButtonClick={() => setShowUploadModal(false)}
        variant="fixed"
        footerContent={
          isShowModalAction ? (
            <Text color="secondary" size="small">
              Make sure you’ve selected MIDs. You will be able to update it
              later.
            </Text>
          ) : null
        }
        onAction={onModalAction}
        actions={isShowModalAction ? MODAL_ACTIONS : []}
        title="Upload Images"
        size="full"
        onClickOutside={() => setShowUploadModal(false)}
      >
        <UploadImage
          onUploadImage={onUpload}
          allowMultiple={true}
          allMids={midsItems}
          selectedMids={selectedMID}
          onDelete={onDelete}
          onUpdateEvidence={onUpdateEvidence}
        />
      </Modal>
      <Header
        totalImages={imagesList?.length || 0}
        midSelectItems={midsItems}
        sortSelectItems={sortBySelectItems}
        search={search}
        setSearch={setSearch}
        setShowUploadModal={setShowUploadModal}
        setSelectedMID={onMidSelect}
        setSortBy={setSortBy}
        sortBy={sortBy}
      />
      <ImagesList
        merchantId={selectedMerchant?.id}
        isLoading={loading}
        onEvidenceChange={onEvidenceChange}
        handleOpenDetails={handleOpenDetails}
        images={imagesList}
        isSelected={isSelected}
        onDelete={onDelete}
        onToggleSelect={toggleSelect}
        totalCount={totalImagesCount}
        onPageChange={setCurrentPage}
        onPerPageChange={onPerPageChange}
        currentPage={currentPage}
        perPage={perPage}
      />
      {selectedArray.length > 0 && (
        <SelectedTooltip
          onDelete={onDeleteMany}
          totalCount={imagesList.length}
          selectedCount={selectedArray.length}
          toggleSelectAll={toggleSelectAll}
        />
      )}
      {currentId && (
        <DetailsSidebar
          onEvidenceChange={onEvidenceChange}
          onUpdateEvidence={onUpdateEvidence}
          allMids={midsItems}
          onDelete={onDelete}
          currentId={currentId}
          onClose={() => setCurrentId('')}
        />
      )}
    </div>
  )
}

export default ImageLibrary
